import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import StructuredData from '@/components/base/StructuredData';
import { categories, articles } from '@/mocks/community';

export default function CategoryDetail() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.categoryId === slug);

  if (!category) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 flex items-center justify-center mx-auto rounded-full bg-background-100 text-foreground-400 mb-4">
            <i className="ri-error-warning-line text-3xl"></i>
          </div>
          <h1 className="font-heading text-xl font-bold text-foreground-900 mb-2">分类未找到</h1>
          <p className="text-sm text-foreground-500 mb-4">您访问的分类不存在或已被移除</p>
          <Link
            to="/categories"
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            返回分类列表
          </Link>
        </div>
      </div>
    );
  }

  const webPageSchema = {
    name: `${category.name} - 海角社区`,
    description: category.description,
    url: `${window.location.origin}/category/${slug}`,
  };

  return (
    <div className="min-h-screen pt-20">
      <StructuredData type="WebPage" data={webPageSchema} />

      {/* Category Header */}
      <div className="relative h-56 md:h-72 overflow-hidden">
        <img
          src={category.coverImage}
          alt={category.name}
          title={category.name}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-black/15"></div>
        <div className="absolute bottom-0 left-0 w-full px-4 md:px-6 max-w-7xl mx-auto pb-8">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '话题分类', to: '/categories' },
              { label: category.name },
            ]}
          />
          <div className="flex items-center gap-3 mt-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/20 backdrop-blur-sm text-white">
              <i className={`${category.icon} text-lg`}></i>
            </div>
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-white">{category.name}</h1>
              <p className="text-sm text-white/75 mt-1">{category.description}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Articles */}
      <div className="py-10 md:py-14">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-900">
              共 <strong className="text-primary-600">{categoryArticles.length}</strong> 篇文章
            </h2>
            <Link
              to="/categories"
              className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer"
            >
              查看其他分类
            </Link>
          </div>

          {categoryArticles.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center mx-auto rounded-full bg-background-100 text-foreground-400 mb-4">
                <i className="ri-article-line text-2xl"></i>
              </div>
              <p className="text-foreground-500 text-sm">该分类下暂无文章</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categoryArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group cursor-pointer bg-background-50 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-200 transition-all duration-200"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      title={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-500 leading-relaxed mb-3 line-clamp-2">{article.summary}</p>
                    <div className="flex items-center justify-between text-xs text-foreground-400">
                      <div className="flex items-center gap-2">
                        <img src={article.authorAvatar} alt={article.author} className="w-5 h-5 rounded-full object-cover" />
                        <span>{article.author}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line text-xs"></i>
                          {article.readCount >= 1000 ? `${(article.readCount / 1000).toFixed(1)}k` : article.readCount}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-heart-line text-xs"></i>
                          {article.likeCount}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Other Categories */}
      <section className="py-10 md:py-14 bg-background-100">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-900 mb-6">探索其他分类</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
            {categories
              .filter((c) => c.slug !== slug)
              .map((c) => (
                <Link
                  key={c.id}
                  to={`/category/${c.slug}`}
                  className="flex items-center gap-2 p-3 rounded-lg bg-background-50 hover:bg-primary-50 transition-colors duration-200 cursor-pointer border border-background-200/60"
                >
                  <div className="w-7 h-7 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-600">
                    <i className={`${c.icon} text-sm`}></i>
                  </div>
                  <span className="text-sm font-medium text-foreground-700 whitespace-nowrap">{c.name}</span>
                </Link>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
}